/**
 * Decides whether the feedback widget should activate (bootstrap) on the current
 * page — the overall wrapper/gate for the whole widget.
 *
 * Always on:
 *   - localhost / loopback (development), and
 *   - any brafton.com host or subdomain — EXCEPT `www.brafton.com` (the public
 *     marketing site).
 *
 * On any other host the widget activates ONLY when the page URL carries the
 * `brafton-feedback=true` query parameter.
 */
export function shouldActivateWidget(location: Pick<Location, 'hostname' | 'search'>): boolean {
  const hostname = (location.hostname || '').toLowerCase();

  if (isLoopbackHost(hostname) || isAllowedBraftonHost(hostname)) {
    return true;
  }
  return hasActivationParam(location.search);
}

/**
 * Whether this host must be verified against the round's allowed-domain list
 * before the widget may finish initializing.
 *
 * Loopback (development) and Brafton hosts are implicitly trusted, so no server
 * check is needed. Any OTHER host only reached activation via the
 * `?brafton-feedback=true` param (see {@link shouldActivateWidget}), so it must
 * be confirmed against the project's allowlist via the embed-allowed endpoint.
 */
export function requiresEmbedAllowedCheck(location: Pick<Location, 'hostname'>): boolean {
  const hostname = (location.hostname || '').toLowerCase();
  return !(isLoopbackHost(hostname) || isAllowedBraftonHost(hostname));
}

/** localhost, *.localhost, and IPv4/IPv6 loopback. */
function isLoopbackHost(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname.endsWith('.localhost') ||
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname === '[::1]'
  );
}

/** `brafton.com` and any subdomain, except the public `www.brafton.com`. */
function isAllowedBraftonHost(hostname: string): boolean {
  if (hostname === 'www.brafton.com') {
    return false;
  }
  // Leading dot in the suffix check prevents `notbrafton.com` from matching.
  return hostname === 'brafton.com' || hostname.endsWith('.brafton.com');
}

/** True when the page URL has `?brafton-feedback=true`. */
function hasActivationParam(search: string): boolean {
  return new URLSearchParams(search).get('brafton-feedback') === 'true';
}
